import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Flame, ChevronRight, Lock } from "lucide-react";
import { getProfile } from "@/data/practiceData";
import PageBlobs from "@/components/PageBlobs";
import UserAvatar from "@/components/UserAvatar";
import SessionDownloadButton, { isPaidPlan, hasPremiumAnalytics } from "@/components/SessionDownload";
import { apiFetch, getToken } from "@/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
};

const eyebrow = "text-xs font-bold uppercase tracking-[0.2em]";
const card = "rounded-[1.5rem] border border-[#E3ECE6] bg-white";

const CalendarGrid = ({ practicedDates }) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const today = now.getDate();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const monthName = now.toLocaleString("en-US", { month: "long", year: "numeric" });

  const practiced = (d) =>
    practicedDates.has(
      `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`
    );

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div data-testid="calendar-grid">
      <p className={`${eyebrow} text-[#647067]`}>{monthName}</p>
      <div className="mt-4 grid grid-cols-7 gap-1.5 sm:gap-2">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <span
            key={i}
            className="text-center text-[10px] font-bold uppercase text-[#647067]"
          >
            {d}
          </span>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <span key={`empty-${i}`} />
          ) : (
            <span
              key={d}
              data-testid={`calendar-day-${d}`}
              className={`flex aspect-square items-center justify-center rounded-full text-[11px] font-bold transition-transform duration-200 ${
                practiced(d)
                  ? "bg-[#16A34A] text-white"
                  : "border-2 border-[#E3ECE6] text-[#647067]/50"
              } ${d === today ? "ring-2 ring-[#FACC15] ring-offset-2" : ""}`}
            >
              {d}
            </span>
          )
        )}
      </div>
    </div>
  );
};

const Progress = () => {
  const navigate = useNavigate();
  getProfile();
  const [streak, setStreak] = useState(0);
  const [longest, setLongest] = useState(0);
  const [history, setHistory] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [user, setUser] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me")
      .then((u) => {
        setStreak(u.currentStreak ?? 0);
        setLongest(u.longestStreak ?? 0);
        setUser(u);
      })
      .catch(() => {});
    apiFetch("/practice-sessions")
      .then(setHistory)
      .catch(() => {});
    // full-history aggregates (the list above is capped at the 20 most recent)
    apiFetch("/practice-sessions/stats")
      .then(setStatsData)
      .catch(() => {});
  }, []);

  const openSession = (id) => {
    setDetail({ id });
    setDetailLoading(true);
    apiFetch(`/practice-sessions/${id}`)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const stats = [
    {
      label: "Total Questions Practiced",
      value:
        statsData?.totalQuestions ??
        history.reduce((a, s) => a + (s.questionsCompleted || 0), 0),
    },
    { label: "Total Practice Sessions", value: statsData?.totalSessions ?? history.length },
    {
      label: "Topics Covered",
      value: statsData?.topicsCovered ?? new Set(history.map((s) => s.chapter)).size,
    },
    {
      label: "Subjects Practiced",
      value: statsData?.subjectsPracticed ?? new Set(history.map((s) => s.subject)).size,
    },
  ];

  const weekDaysLocal = [...Array(7)].map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return {
      key: d.toDateString(),
      label: d.toLocaleDateString("en-US", { weekday: "short" }),
      total: 0,
    };
  });
  history.forEach((s) => {
    if (!s.completedAt) return;
    const day = weekDaysLocal.find(
      (d) => d.key === new Date(s.completedAt).toDateString()
    );
    if (day) day.total += s.questionsCompleted || 0;
  });
  // real last-7-days totals from the uncapped stats endpoint when available
  const weekDays = statsData?.last7Days?.length
    ? statsData.last7Days.map((d) => ({ key: d.date, label: d.label, total: d.total }))
    : weekDaysLocal;
  const maxDay = Math.max(...weekDays.map((d) => d.total), 1);

  return (
    <div
      data-testid="progress-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative z-40 border-b border-[#E3ECE6] bg-[#F8FAF7]/85 backdrop-blur-md">
        <div className="mx-auto flex w-[94%] max-w-[1520px] items-center justify-between py-5">
          <Link
            to="/"
            data-testid="logo-home-link"
            className="font-display text-2xl font-extrabold"
          >
            <img src="/logo.png" alt="Abhyas" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            {user && (
              <span
                data-testid="streak-badge"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#FACC15]/25 px-4 py-2 text-sm font-bold text-[#17201A]"
              >
                <Flame className="h-4 w-4 text-[#FACC15]" />
                {streak}
              </span>
            )}
            <UserAvatar />
          </div>
        </div>
      </header>

      <main className="relative mx-auto w-[94%] max-w-[1520px] pb-20">
        <div className="mt-10 animate-fade-up">
          <h1 className="font-display text-3xl font-extrabold tracking-[-0.02em] sm:text-4xl">
            Your <span className="italic text-[#16A34A]">progress</span>
          </h1>
          <p className="mt-2 text-sm font-semibold text-[#647067]">
            Every session counts — here's how your practice is adding up.
          </p>
        </div>

        {user && (
          <>

        {/* Streak overview + calendar */}
        <div
          data-testid="streak-overview"
          className={`${card} animate-fade-up mt-8 grid gap-10 p-8 sm:p-10 lg:grid-cols-5`}
          style={{ animationDelay: "70ms" }}
        >
          <div className="lg:col-span-2">
            <p className={`${eyebrow} text-[#B45309]`}>Current streak</p>
            <div className="mt-4 flex items-center gap-4">
              <div className="animate-pulse-soft rounded-full bg-[#FACC15]/25 p-5">
                <Flame className="h-9 w-9 text-[#FACC15]" />
              </div>
              <div>
                <p
                  data-testid="current-streak"
                  className="font-display text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl"
                >
                  {streak} Day Streak
                </p>
                <p className="mt-1 text-sm font-semibold text-[#647067]">
                  Longest streak:{" "}
                  <span data-testid="longest-streak" className="text-[#16A34A]">
                    {longest} days
                  </span>
                </p>
              </div>
            </div>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#647067]">
              Practice once a day to keep the flame alive — even 5 questions
              count.
            </p>
          </div>
          <div className="lg:col-span-3">
            {isPaidPlan(user) ? (
              <CalendarGrid
                practicedDates={
                  statsData?.practicedDates
                    ? new Set(statsData.practicedDates)
                    : new Set(
                        history
                          .filter((s) => s.completedAt)
                          .map((s) => s.completedAt.slice(0, 10))
                      )
                }
              />
            ) : (
              <div
                data-testid="calendar-locked"
                className="flex h-full min-h-[180px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#E3ECE6] p-8 text-center"
              >
                <Lock className="h-5 w-5 text-[#94A39B]" />
                <p className="mt-3 text-sm font-bold text-[#17201A]">
                  Calendar &amp; long-term streak insights unlock with a plan
                </p>
                <button
                  data-testid="calendar-unlock-btn"
                  onClick={() => navigate("/pricing")}
                  className="mt-4 rounded-full bg-[#16A34A] px-6 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
                >
                  View plans
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Overall stats — unlocked for all paid plans (Starter and above) */}
        <div
          data-testid="progress-stats-row"
          className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              data-testid={`progress-stat-${i}`}
              className={`${card} animate-fade-up p-6 transition-[border-color,transform] duration-200 hover:border-[#16A34A]/40`}
              style={{ animationDelay: `${140 + i * 70}ms` }}
            >
              <p className={`${eyebrow} text-[#647067]`}>{s.label}</p>
              <p className="mt-2 font-display text-3xl font-extrabold text-[#17201A]">
                {s.value}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-5">
          {/* Daily activity chart — unlocked for all paid plans (Starter and above) */}
          {isPaidPlan(user) ? (
          <div
            data-testid="weekly-chart"
            className={`${card} animate-fade-up p-7 lg:col-span-2`}
            style={{ animationDelay: "420ms" }}
          >
            <p className={`${eyebrow} text-[#647067]`}>Last 7 days</p>
            <div className="mt-6 flex h-40 items-end justify-between gap-3">
              {weekDays.map((d, i) => (
                <div key={i} className="flex flex-1 flex-col items-center gap-2">
                  <span className="text-[10px] font-bold text-[#647067]">
                    {d.total > 0 ? d.total : ""}
                  </span>
                  <div
                    data-testid={`chart-day-${i}`}
                    className={`w-full rounded-t-lg transition-[height] duration-500 ${
                      d.total > 0 ? "bg-[#16A34A]" : "bg-[#E3ECE6]"
                    }`}
                    style={{
                      height: `${d.total > 0 ? (d.total / maxDay) * 112 : 6}px`,
                    }}
                  />
                  <span className="text-[10px] font-bold uppercase text-[#647067]">
                    {d.label}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs font-semibold text-[#647067]">
              Questions practiced per day
            </p>
          </div>
          ) : (
          <div
            data-testid="weekly-chart-locked"
            className={`${card} animate-fade-up flex flex-col items-center justify-center p-7 text-center lg:col-span-2`}
            style={{ animationDelay: "420ms" }}
          >
            <Lock className="h-5 w-5 text-[#94A39B]" />
            <p className="mt-3 text-sm font-bold text-[#17201A]">
              Daily statistics &amp; 7-day chart unlock with a plan
            </p>
            <button
              data-testid="chart-unlock-btn"
              onClick={() => navigate("/pricing")}
              className="mt-4 rounded-full bg-[#16A34A] px-6 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
            >
              View plans
            </button>
          </div>
          )}

          {/* Practice history */}
          <div
            className="animate-fade-up lg:col-span-3"
            style={{ animationDelay: "490ms" }}
          >
            <h2 className="font-display text-xl font-extrabold sm:text-2xl">
              Practice history
            </h2>
            <div
              data-testid="history-list"
              className={`${card} mt-4 divide-y divide-[#E3ECE6] px-6`}
            >
              {history.length === 0 && (
                <p
                  data-testid="history-empty"
                  className="py-6 text-sm font-semibold text-[#647067]"
                >
                  No practice sessions yet — finish your first set to see it here.
                </p>
              )}
              {(isPaidPlan(user) ? history : history.slice(0, 3)).map((s, i) => (
                <button
                  key={s.id || i}
                  data-testid={`history-item-${i}`}
                  onClick={() => openSession(s.id)}
                  className="group flex w-full items-center justify-between gap-4 py-5 text-left transition-colors duration-200"
                >
                  <div>
                    <p className="font-display text-base font-bold transition-colors duration-200 group-hover:text-[#16A34A]">
                      {s.chapter}
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-[#647067]">
                      {s.subject} · {formatDate(s.completedAt)} · {s.questionsCompleted} questions
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-[#16A34A]/10 px-4 py-1.5 text-xs font-bold text-[#16A34A]">
                      {s.questionsCompleted} Q
                    </span>
                    <ChevronRight className="h-4 w-4 text-[#647067] transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[#16A34A]" />
                  </div>
                </button>
              ))}
            </div>
            {!isPaidPlan(user) && history.length > 3 && (
              <button
                data-testid="history-unlock-btn"
                onClick={() => navigate("/pricing")}
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#16A34A] transition-colors duration-200 hover:text-[#15803D]"
              >
                <Lock className="h-3.5 w-3.5" />
                Full history unlocks with a plan
              </button>
            )}
          </div>
        </div>
          </>
        )}
      </main>

      {/* Session details modal */}
      {detail && (
        <div
          data-testid="session-detail-modal"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6"
          onClick={() => setDetail(null)}
        >
          <div
            className="animate-fade-up flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-xl sm:rounded-[2rem]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#E3ECE6] p-6 sm:p-7">
              <h2 className="font-display text-xl font-extrabold">
                {detailLoading || !detail.chapter ? "Loading session..." : detail.chapter}
              </h2>
              {detail.chapter && (
                <p className="mt-1 text-xs font-semibold text-[#647067]">
                  {detail.subject} · Class {detail.klass} · {detail.board} ·{" "}
                  {formatDate(detail.completedAt)}
                </p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-6 sm:p-7">
              {(detail.questions || []).length > 0 ? (
                <div className="space-y-5">
                  {detail.questions.map((q, i) => (
                    <div key={i} data-testid={`session-question-${i}`} className="rounded-2xl border border-[#E3ECE6] p-4">
                      <p className="text-sm font-bold leading-snug text-[#17201A]">
                        Q{i + 1}. {q.question}
                      </p>
                      <div className="mt-2.5 space-y-1 text-xs font-semibold">
                        {q.student_answer != null && (
                          <p className="text-[#647067]">
                            Your answer: <span className="text-[#17201A]">{q.student_answer}</span>
                          </p>
                        )}
                        <p className="text-[#647067]">
                          {q.question_type === "STATEMENT_THEORY" ? "Model answer" : "Correct answer"}:{" "}
                          <span className="text-[#17201A]">{q.answer}</span>
                        </p>
                        {q.evaluation ? (
                          <p
                            data-testid={`session-eval-${i}`}
                            className={`font-bold ${
                              q.evaluation === "correct"
                                ? "text-[#16A34A]"
                                : q.evaluation === "partially_correct"
                                  ? "text-[#92600A]"
                                  : "text-red-500"
                            }`}
                          >
                            AI Evaluation:{" "}
                            {q.evaluation === "correct"
                              ? "Correct"
                              : q.evaluation === "partially_correct"
                                ? "Partially Correct"
                                : "Incorrect"}
                            {typeof q.marks === "number" ? ` · ${q.marks}/1` : ""}
                          </p>
                        ) : (
                          <>
                            {q.is_correct === true && (
                              <p className="font-bold text-[#16A34A]">Correct</p>
                            )}
                            {q.is_correct === false && (
                              <p className="font-bold text-red-500">Wrong</p>
                            )}
                          </>
                        )}
                        {q.feedback && (
                          <p
                            data-testid={`session-feedback-${i}`}
                            className="text-[#647067]"
                          >
                            Feedback: <span className="text-[#17201A]">{q.feedback}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !detailLoading && detail.chapter && (
                  <p className="text-sm leading-relaxed text-[#647067]">
                    This session was saved before detailed question storage existed — only the
                    summary (and score, where available) can be shown.
                  </p>
                )
              )}
              {detail.questionsCorrect != null && detail.questionsCompleted != null && (
                <p data-testid="session-score" className="mt-5 text-sm font-bold text-[#17201A]">
                  Score: {detail.questionsCorrect}/{detail.questionsCompleted} · Accuracy:{" "}
                  {detail.questionsCompleted
                    ? Math.round((detail.questionsCorrect / detail.questionsCompleted) * 100)
                    : 0}
                  %
                </p>
              )}
            </div>
            <div className="border-t border-[#E3ECE6] p-5 sm:px-7">
              <div className="flex flex-wrap items-center gap-3">
                <SessionDownloadButton
                  sessionId={detail.id}
                  paid={hasPremiumAnalytics(user)}
                  onLockedClick={() => navigate("/pricing")}
                />
                {detail.chapter && (
                  <button
                    data-testid="session-practice-again-btn"
                    onClick={() =>
                      navigate("/practice-setup", {
                        state: {
                          quickStart: {
                            subject: detail.subject,
                            chapter: detail.chapter,
                            klass: detail.klass,
                            board: detail.board,
                            difficulty: detail.difficulty,
                          },
                        },
                      })
                    }
                    className="text-sm font-bold text-[#16A34A] hover:underline"
                  >
                    Practice again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Progress;
